import React, { useCallback, useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  CommerceAddress,
  FetchShipToGroupedQuery,
  FetchShipToGroupedQueryVariables,
  PartnerSearchContext,
} from '@css-xp/graphtypes/src/generated-types-new';
import { DEFAULT_PAGE, DEFAULT_ROWS_PER_PAGE } from '@atlassian/cssxp-utils-entitlement';
import { SoldToAddressTable } from './sold-to-address-table';
import { fetchShipToGrouped } from './queries/fetch-ship-to-grouped';

interface Props {
  txaId: string;
  billToAddress?: CommerceAddress | null;
  availablePaymentMethods?: any;
  hidePricingEligibility?: boolean;
  showAddAddress?: boolean;
  hideActions?: boolean;
  showSearchBox?: boolean;
  searchTerm?: string;
  hideEdit?: boolean;
  hideAddFromBillToAddress?: boolean;
  partnerSearchContext?: string;
  showAddAddressInRow?: boolean;
  heading?: React.ReactNode;
}

export const SoldToAddressGroupedForTxa = ({
  txaId,
  billToAddress,
  availablePaymentMethods,
  hidePricingEligibility,
  showAddAddress,
  hideActions,
  showSearchBox,
  searchTerm = '',
  hideEdit,
  hideAddFromBillToAddress,
  partnerSearchContext,
  showAddAddressInRow = true,
  heading,
}: Props) => {
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE);

  const { data, loading, refetch, error } = useQuery<FetchShipToGroupedQuery, FetchShipToGroupedQueryVariables>(
    fetchShipToGrouped,
    {
      variables: {
        searchParams: {
          query: searchTerm,
          partnerSearchContext:
            !partnerSearchContext || partnerSearchContext === 'ALL'
              ? null
              : (partnerSearchContext as PartnerSearchContext),
          transactionAccountId: txaId,
        },
        pageParams: {
          size: rowsPerPage,
          cursor: null,
          offset: 0,
          first: 8,
        },
      },
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-and-network',
      skip: !txaId,
    }
  );

  const onPageChange = useCallback(
    (newPage: number) => {
      setCurrentPage(newPage);
      refetch({
        pageParams: {
          first: rowsPerPage,
          offset: (newPage - 1) * rowsPerPage,
        },
      });
    },
    [rowsPerPage, currentPage, data]
  );

  const handleRowChange = useCallback(
    (newRowPerPage: number) => {
      if (newRowPerPage === rowsPerPage) {
        return;
      }
      setRowsPerPage(newRowPerPage);
      setCurrentPage(1);
      refetch({
        pageParams: {
          first: newRowPerPage,
          offset: 0,
        },
      });
    },
    [rowsPerPage]
  );

  const onSearch = useCallback((searchTerm: string) => {
    refetch({
      searchParams: {
        query: searchTerm,
        transactionAccountId: txaId,
      },
      pageParams: {
        first: rowsPerPage,
        offset: 0,
      },
    });
  }, []);
  const response = data?.shipToPartyGrouped;
  return (
    <div data-testid="transaction-account-ship-to-address">
      <SoldToAddressTable
        heading={heading}
        error={error}
        addressResponse={response}
        loading={loading}
        transactionAccountId={txaId}
        billToAddress={billToAddress}
        onSearch={onSearch}
        availablePaymentMethods={availablePaymentMethods}
        hidePricingEligibility={hidePricingEligibility}
        showAddAddress={showAddAddress}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        onRowChange={handleRowChange}
        onPageChange={onPageChange}
        hideActions={hideActions}
        showSearchBox={showSearchBox}
        refetch={refetch}
        hideEdit={hideEdit}
        hideAddFromBillToAddress={hideAddFromBillToAddress}
        isGroupedData={true}
        showAddAddressInRow={showAddAddressInRow}
      />
    </div>
  );
};
